/**
 * apps/command/src/screens/LoginScreen.tsx
 *
 * The SAME credentials as the murojaat panel, because it is the same session
 * (§2.1). An official who is already signed in at `/` never sees this screen —
 * `GET /admin/me` succeeds on the shared `__Host-nvadmin` cookie and the app
 * goes straight to their home.
 *
 * FAILURE IS UNDIFFERENTIATED on purpose. A wrong password, an unknown address
 * and a wrong TOTP all produce one message: telling an attacker which of the
 * three they got right is how an account enumeration works. The two exceptions
 * are states the account holder needs in order to act — a lockout and a rate
 * limit, both of which mean "wait", and neither of which reveals whether the
 * address exists.
 */
import { useState, type FormEvent } from 'react';
import { ApiError } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { t } from '../i18n/index.js';
import { Button, Field, Notice, Panel, TextInput } from '../ui/index.jsx';
import { Gerb } from '../ui/Gerb.jsx';

export function LoginScreen(): React.JSX.Element {
  const { login } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password, totp);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 429) setError(t('login.rateLimited'));
      else if (caught instanceof ApiError && caught.status === 423) setError(t('login.locked'));
      else setError(t('login.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex h-full items-center justify-center bg-canvas px-6 text-ink-100">
      <Panel className="w-full max-w-sm p-8">
        {/*
          THE FORMAL LOCKUP: emblem, institution, system — in that order.

          The institution is the larger line and the software's name is beneath
          it, because this is the screen an official meets first and the thing
          they are signing in to is a hokimlik's instrument rather than a
          product. Caps as a display treatment (`uppercase`), never by shouting
          in the dictionary — the same rule the kiosk's attract lockup follows,
          so the string stays a normal sentence for every other reader of it.
        */}
        <div className="mb-6 flex flex-col items-center text-center">
          <Gerb className="h-20 w-auto" />
          <h1 className="mt-4 text-label font-semibold uppercase tracking-wide text-ink-100">
            {t('app.institution')}
          </h1>
          <p className="mt-1 text-micro text-ink-500">{t('login.title')}</p>
          {/* §15.4's gold hairline, the one official accent on this screen. */}
          <div className="mt-4 h-px w-full bg-gold/50" />
        </div>

        <form className="space-y-4" onSubmit={(event) => void submit(event)}>
          <Field label={t('login.email')}>
            <TextInput
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Field label={t('login.password')}>
            <TextInput
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </Field>
          <Field label={t('login.totp')} hint={t('login.totpHint')}>
            <TextInput
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totp}
              onChange={(event) => setTotp(event.target.value)}
            />
          </Field>

          {error ? <Notice tone="crit">{error}</Notice> : null}

          <Button type="submit" disabled={busy} className="w-full">
            {busy ? t('app.loading') : t('login.submit')}
          </Button>
        </form>
      </Panel>
    </div>
  );
}
